import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

// Operator data with icons and colors (fallback)
const operators = [
  { id: '100', name: 'Airtel', icon: "A", color: "#E40000", bgColor: "#E4000015" },
  { id: '101', name: 'Jio', icon: "J", color: "#0078FF", bgColor: "#0078FF15" },
  { id: '102', name: 'Vi', icon: "V", color: "#6A0DAD", bgColor: "#6A0DAD15" },
  { id: '103', name: 'BSNL', icon: "B", color: "#FF6600", bgColor: "#FF660015" },
];

const circles = [
  { id: '1', name: 'Andhra Pradesh' },
  { id: '2', name: 'Assam' },
  { id: '3', name: 'Bihar & Jharkhand' },
  { id: '4', name: 'Chennai' },
  { id: '5', name: 'Delhi & NCR' },
  { id: '6', name: 'Gujarat' },
  { id: '7', name: 'Haryana' },
  { id: '8', name: 'Himachal Pradesh' },
  { id: '9', name: 'Jammu & Kashmir' },
  { id: '10', name: 'Karnataka' },
  { id: '11', name: 'Kerala' },
  { id: '12', name: 'Kolkata' },
  { id: '13', name: 'Maharashtra & Goa (except Mumbai)' },
  { id: '14', name: 'MP & Chhattisgarh' },
  { id: '15', name: 'Mumbai' },
  { id: '16', name: 'North East' },
  { id: '17', name: 'Orissa' },
  { id: '18', name: 'Punjab' },
  { id: '19', name: 'Rajasthan' },
  { id: '20', name: 'Tamilnadu / Telangana' },
  { id: '21', name: 'UP (East)' },
  { id: '22', name: 'UP (West) & Uttarakhand' },
  { id: '23', name: 'West Bengal' },
  { id: '51', name: 'All India (except Delhi/Mumbai)' }
];

function MobileRechargePlan() {
  const navigate = useNavigate();
  const location = useLocation();
  const [planValidationLoading, setPlanValidationLoading] = useState(false);

  const {
    number = null,
    circle = null,
    circle_id = null,
    operator = null,
    operator_id = null,
    operator_logo = null
  } = location.state || {};

  const [activeTab, setActiveTab] = useState("");
  const [tabs, setTabs] = useState([]);
  const [plans, setPlans] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [planFetching, setPlanFetching] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [operatorLogo, setOperatorLogo] = useState(operator_logo);

  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editOperatorId, setEditOperatorId] = useState(operator_id || '');
  const [editCircleId, setEditCircleId] = useState(circle_id || '');
  const [isSaving, setIsSaving] = useState(false);

  // Local state for displaying current operator and circle names
  const [currentCircleName, setCurrentCircleName] = useState(circle);
  const [currentOperatorName, setCurrentOperatorName] = useState(operator);

  // Refs for swipe functionality
  const tabContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchStartY, setTouchStartY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const getSelectedOperatorObject = () => {
    const foundOperator = operators.find(op => op.id === operator_id);
    return foundOperator || operators.find(op => op.name === currentOperatorName) || operators[0];
  };

  const [selectedOperator, setSelectedOperator] = useState(getSelectedOperatorObject());

  const nonIndividualTabs = tabs.filter(tab => tab.toLowerCase() !== 'individual');

  const getAllPlans = () => {
    return nonIndividualTabs.flatMap(tab => plans[tab] || []);
  };

  const getFilteredPlans = () => {
    let result = [];

    if (!searchQuery) {
      result = activeTab ? (plans[activeTab] || []) : [];
    } else {
      // Check if searchQuery is a valid number
      const isNumeric = !isNaN(searchQuery) && searchQuery !== "";
      const searchAmount = isNumeric ? parseInt(searchQuery) : null;

      // Find plans that match the search (partial match on amount)
      const matchedPlans = getAllPlans().filter(plan =>
        plan.amount?.toString().includes(searchQuery)
      );

      // Check if there's an exact match
      const exactMatch = searchAmount !== null && matchedPlans.some(
        plan => plan.amount === searchAmount
      );

      if (isNumeric && searchAmount > 0) {
        // If no exact match, add custom plan on top
        if (!exactMatch) {
          const customPlan = {
            amount: searchAmount,
            planDescription: `Custom plan for ₹${searchAmount}`,
            Validity: "N/A",
            Data: "N/A",
            planBenefitItemList: [],
            isCustomPlan: true
          };
          // Custom plan goes first, then matched plans
          result = [customPlan, ...matchedPlans];
        } else {
          // Exact match found, show only matched plans (no custom plan)
          result = matchedPlans;
        }
      } else {
        // Non-numeric search or empty search
        result = matchedPlans;
      }
    }

    // Sort by amount (lowest first) but keep custom plan at top if present
    // Separate custom plans from regular plans
    const customPlans = result.filter(p => p.isCustomPlan);
    const regularPlans = result.filter(p => !p.isCustomPlan);

    // Sort regular plans by amount
    regularPlans.sort((a, b) => a.amount - b.amount);

    // Return custom plans first, then sorted regular plans
    return [...customPlans, ...regularPlans];
  };

  const filteredPlans = getFilteredPlans();

  const getFirstLine = (description) => {
    if (!description) return "";
    const firstLine = description.split('\n')[0];
    if (firstLine.length > 50) {
      return firstLine.substring(0, 47) + "...";
    }
    return firstLine;
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);
    setIsSwiping(false);
  };

  const handleTouchMove = (e) => {
    if (!touchStartX) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX;
    const deltaY = touch.clientY - touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      setIsSwiping(true);
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    if (!isSwiping) {
      setTouchStartX(0);
      setTouchStartY(0);
      setIsSwiping(false);
      return;
    }

    const deltaX = touchStartX - e.changedTouches[0].clientX;
    const minSwipeDistance = 30;

    if (Math.abs(deltaX) > minSwipeDistance) {
      const currentIndex = nonIndividualTabs.indexOf(activeTab);

      if (deltaX > 0 && currentIndex < nonIndividualTabs.length - 1) {
        const nextTab = nonIndividualTabs[currentIndex + 1];
        setActiveTab(nextTab);
        setSearchQuery('');
        scrollTabIntoView(nextTab);
      } else if (deltaX < 0 && currentIndex > 0) {
        const prevTab = nonIndividualTabs[currentIndex - 1];
        setActiveTab(prevTab);
        setSearchQuery('');
        scrollTabIntoView(prevTab);
      }
    }

    setTouchStartX(0);
    setTouchStartY(0);
    setIsSwiping(false);
  };

  const scrollTabIntoView = (tabName) => {
    if (tabContainerRef.current) {
      const tabButtons = tabContainerRef.current.querySelectorAll('button');
      const targetIndex = nonIndividualTabs.indexOf(tabName);
      if (tabButtons[targetIndex]) {
        tabButtons[targetIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  };

  const handleSelect = async (plan) => {
    setSelectedPlan(plan);
    setPlanValidationLoading(true);
    try {
      const result = await apiCall(
        "recharge-validation",
        {
          operator_id: editOperatorId,
          circle_code: editCircleId || circle_id,
          plan_amount: plan.amount,
          number: number,
        },
        true
      );
      if (result.status === true) {
        navigate("/payment-confirmation", {
          state: {
            service: "Mobile Recharge",
            operator: selectedOperator.name,
            logo: operatorLogo,
            operator_id: editOperatorId,
            number: number,
            amount: plan.amount,
            consumerName: 'NA',
            serviceSpecificData: {
              circle: currentCircleName,
              circle_id: editCircleId || circle_id,
              data_pack: plan.Data,
              validaty: plan.Validity,
              talktime: plan.Talktime,
              planDesc: plan.planDescription
            }
          }
        });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error sending data:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setPlanValidationLoading(false);
    }
  };

  const handleViewDetails = (plan, e) => {
    e.stopPropagation();
    setSelectedPlanDetails(plan);
    setShowDetailsSheet(true);
  };

  const fetchPlan = useCallback(async (opId, circId) => {
    if (number?.length !== 10 || !opId || !circId) {
      setPlanFetching(false);
      return;
    }

    setPlanFetching(true);
    try {
      const result = await apiCall(
        "recharge-plan",
        {
          operator_id: opId,
          circle_code: circId
        },
        true
      );
      if (result.status === true) {
        if (result.operator_data?.operator_logo) {
          setOperatorLogo(result.operator_data.operator_logo);
        }

        setPlans(result.plans);
        const planTabs = Object.keys(result.plans);
        setTabs(planTabs);
        setActiveTab(planTabs[0]);
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setPlanFetching(false);
    }
  }, [number]);

  const handleSaveChanges = async () => {
    if (!editOperatorId || !editCircleId) {
      toast.error("Please select both operator and circle");
      return;
    }

    setIsSaving(true);
    try {
      // Find the selected operator and circle names
      const selectedOp = operators.find(op => op.id === editOperatorId);
      const selectedCirc = circles.find(c => c.id === editCircleId);

      if (selectedOp && selectedCirc) {
        // Update the selected operator object
        setSelectedOperator(selectedOp);

        // Update the display names
        setCurrentOperatorName(selectedOp.name);
        setCurrentCircleName(selectedCirc.name);

        // Update the parent state variables
        // Note: These are from location.state and are read-only in this component
        // We'll use the new values for the fetch
        await fetchPlan(editOperatorId, editCircleId);

        // Force a re-render with new values by updating a state
        setIsEditMode(false);
        toast.success("Operator and circle updated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = () => {
    setEditOperatorId(operator_id || '');
    setEditCircleId(circle_id || '');
    setIsEditMode(true);
  };

  useEffect(() => {
    fetchPlan(operator_id, circle_id);
  }, [fetchPlan, operator_id, circle_id]);

  useEffect(() => {
    // Update local state when location state changes
    if (circle) {
      setCurrentCircleName(circle);
    }
    if (operator) {
      setCurrentOperatorName(operator);
    }
  }, [circle, operator]);

  const tabContainerStyle = {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  };

  // Full screen loading overlay
  if (planFetching) {
    return (
      <>
        <Header title="Recharge Plan" showBack={true} />
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
              style={{
                borderColor: `${APP_THEME.primaryColor}33`,
                borderTopColor: APP_THEME.primaryColor,
              }}
            />
            <div
              className="text-sm font-medium"
              style={{ color: APP_THEME.textPrimary }}
            >
              Loading plans...
            </div>
          </div>
        </div>
        <Toaster position="bottom-center" />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col" style={{ background: APP_THEME.bgColor }}>
      <Header title="Recharge Plan" showBack={true} />

      {/* Plan Validation Loading Overlay */}
      {planValidationLoading && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[1200]"
          style={{
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
              style={{
                borderColor: `${APP_THEME.primaryColor}33`,
                borderTopColor: APP_THEME.primaryColor,
              }}
            />
            <div
              className="text-sm font-medium"
              style={{ color: APP_THEME.textPrimary }}
            >
              Validating...
            </div>
          </div>
        </div>
      )}

      <div className="flex-shrink-0">
        {/* Operator Card - with Edit Mode */}
        <div className="px-4 pt-4 pb-2">
          <div
            className="rounded-2xl p-3 flex items-center justify-between"
            style={{
              background: APP_THEME.surfaceColor,
              border: `1px solid ${APP_THEME.borderColor}`,
            }}
          >
            {!isEditMode ? (
              // Display Mode
              <>
                <div className="flex items-center gap-3">
                  {operatorLogo ? (
                    <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-white">
                      <img
                        src={operatorLogo}
                        alt={selectedOperator.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-xl font-bold"
                      style={{
                        background: selectedOperator.bgColor,
                        color: selectedOperator.color,
                      }}
                    >
                      {selectedOperator.icon}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-bold" style={{ color: APP_THEME.textPrimary }}>
                      {selectedOperator.name} | {number}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: APP_THEME.textSecondary }}>
                      {currentCircleName}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleEditClick}
                  className="p-2 rounded-xl active:scale-95 transition-transform"
                  style={{ background: `${APP_THEME.primaryColor}10` }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.primaryColor} strokeWidth="2">
                    <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                    <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                  </svg>
                </button>
              </>
            ) : (
              // Edit Mode
              <div className="w-full space-y-3">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: APP_THEME.textSecondary }}>
                    Operator
                  </label>
                  <select
                    value={editOperatorId}
                    onChange={(e) => setEditOperatorId(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                    style={{
                      background: APP_THEME.bgColor,
                      color: APP_THEME.textPrimary,
                      border: `1px solid ${APP_THEME.borderColor}`,
                    }}
                  >
                    <option value="">Select Operator</option>
                    {operators.map((op) => (
                      <option key={op.id} value={op.id}>
                        {op.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: APP_THEME.textSecondary }}>
                    Circle
                  </label>
                  <select
                    value={editCircleId}
                    onChange={(e) => setEditCircleId(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                    style={{
                      background: APP_THEME.bgColor,
                      color: APP_THEME.textPrimary,
                      border: `1px solid ${APP_THEME.borderColor}`,
                    }}
                  >
                    <option value="">Select Circle</option>
                    {circles.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    className="flex-1 py-2 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50"
                    style={{
                      background: APP_THEME.primaryColor,
                      color: '#fff',
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditMode(false);
                      setEditOperatorId(operator_id || '');
                      setEditCircleId(circle_id || '');
                    }}
                    className="px-4 py-2 rounded-xl text-sm font-semibold transition-all active:scale-95"
                    style={{
                      background: APP_THEME.surfaceColor,
                      color: APP_THEME.textSecondary,
                      border: `1px solid ${APP_THEME.borderColor}`,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Bar */}
        {nonIndividualTabs.length > 0 && (
          <div className="px-4 mb-3">
            <div
              className="flex items-center gap-2 rounded-xl px-4 py-2.5"
              style={{
                background: APP_THEME.surfaceColor,
                border: `1px solid ${APP_THEME.borderColor}`,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="number"
                placeholder="Search by amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none text-sm"
                style={{ color: APP_THEME.textPrimary }}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="p-1 rounded-full active:scale-90 transition-transform">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab with Swipe Support */}
        {nonIndividualTabs.length > 0 && (
          <div className="px-4 pb-2" style={{ background: APP_THEME.bgColor }}>
            <div
              className="overflow-x-auto scrollbar-hide pb-1"
              style={tabContainerStyle}
              ref={tabContainerRef}
            >
              <div className="flex gap-5 min-w-max">
                {nonIndividualTabs.map((cat, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setActiveTab(cat);
                      setSearchQuery('');
                      scrollTabIntoView(cat);
                    }}
                    className="text-sm font-bold whitespace-nowrap pb-2 transition-all"
                    style={{
                      color: activeTab === cat ? APP_THEME.primaryColor : APP_THEME.textSecondary,
                      borderBottom: `2px solid ${activeTab === cat ? APP_THEME.primaryColor : "transparent"}`,
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plans List with Improved Swipe Support */}
      <div
        className="flex-1 overflow-y-auto px-4 pb-4"
        style={{ touchAction: 'pan-y' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={contentContainerRef}
      >
        {nonIndividualTabs.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${APP_THEME.textSecondary}10` }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <p className="text-base font-semibold mb-1" style={{ color: APP_THEME.textPrimary }}>No plans available</p>
            <p className="text-sm" style={{ color: APP_THEME.textSecondary }}>Please try again later</p>
          </div>
        )}

        {nonIndividualTabs.length > 0 && (
          <div className="flex flex-col gap-2">
            {filteredPlans?.map((plan, idx) => {
              const isSelected = selectedPlan?.amount === plan.amount;
              const hasBenefits = Array.isArray(plan.planBenefitItemList) && plan.planBenefitItemList.length > 0;

              return (
                <div
                  key={idx}
                  onClick={() => !showDetailsSheet && handleSelect(plan)}
                  className={`p-3 rounded-xl border cursor-pointer transition-all active:scale-[0.98] ${isSelected ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'
                    }`}
                  style={{
                    background: isSelected ? `${APP_THEME.primaryColor}08` : APP_THEME.surfaceColor,
                  }}
                >
                  {/* Amount */}
                  <div className="text-lg font-bold mb-1.5" style={{ color: APP_THEME.textPrimary }}>
                    ₹{plan.amount}
                  </div>

                  {/* Data & Validity - Compact Row */}
                  <div className="flex items-center gap-3 mb-2 text-xs">
                    <div className="flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                        <path d="M4 4h16v16H4z" />
                        <line x1="8" y1="12" x2="16" y2="12" />
                      </svg>
                      <span style={{ color: APP_THEME.textPrimary }}>{plan.Data || "N/A"}</span>
                    </div>
                    <div className="w-1 h-1 rounded-full" style={{ background: APP_THEME.textSecondary }} />
                    <div className="flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span style={{ color: APP_THEME.textPrimary }}>{plan.Validity || "N/A"}</span>
                    </div>
                  </div>

                  {/* Benefit Icons - Compact */}
                  {hasBenefits && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {plan.planBenefitItemList.slice(0, 3).map((item, idx) => (
                        item.logoUrl && (
                          <div
                            key={idx}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded-md"
                            style={{ background: `${APP_THEME.textSecondary}08` }}
                          >
                            <img src={item.logoUrl} alt="" className="w-3 h-3 object-contain" />
                            <span className="text-[9px]" style={{ color: APP_THEME.textSecondary }}>
                              {item.displayName.length > 12 ? item.displayName.substring(0, 10) + '..' : item.displayName}
                            </span>
                          </div>
                        )
                      ))}
                      {plan.planBenefitItemList.length > 3 && (
                        <div className="px-1.5 py-0.5 rounded-md text-[9px]" style={{ background: `${APP_THEME.textSecondary}08`, color: APP_THEME.textSecondary }}>
                          +{plan.planBenefitItemList.length - 3}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description with See More */}
                  <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: APP_THEME.borderColor }}>
                    <span className="text-[10px] flex-1" style={{ color: APP_THEME.textSecondary }}>
                      {getFirstLine(plan.planDescription)}
                    </span>
                    <button
                      onClick={(e) => handleViewDetails(plan, e)}
                      className="text-[10px] font-semibold ml-2 px-2 py-0.5 rounded-md transition-colors"
                      style={{ color: APP_THEME.primaryColor }}
                    >
                      Details
                    </button>
                  </div>
                </div>
              );
            })}

            {searchQuery && filteredPlans?.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${APP_THEME.textSecondary}10` }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <p className="text-base font-semibold mb-1" style={{ color: APP_THEME.textPrimary }}>No plans found</p>
                <p className="text-sm" style={{ color: APP_THEME.textSecondary }}>Try a different amount</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Sheet - Plan Details */}
      {showDetailsSheet && selectedPlanDetails && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[1100] animate-fadeIn"
            onClick={() => setShowDetailsSheet(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[1101] rounded-t-2xl animate-slideUp max-h-[85vh] overflow-y-auto"
            style={{ background: APP_THEME.bgColor }}
          >
            {/* Header */}
            <div className="sticky top-0 pt-3 pb-2 px-5 border-b" style={{ background: APP_THEME.bgColor, borderColor: APP_THEME.borderColor }}>
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: APP_THEME.borderColor }} />
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold" style={{ color: APP_THEME.textPrimary }}>Plan Details</h3>
                <button onClick={() => setShowDetailsSheet(false)} className="p-1 rounded-full active:scale-90 transition-transform">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5 pb-24">
              {/* Amount */}
              <div className="text-2xl font-extrabold mb-4" style={{ color: APP_THEME.textPrimary }}>
                ₹{selectedPlanDetails.amount}
              </div>

              {/* Data & Validity Cards */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                  <div className="text-xs mb-1" style={{ color: APP_THEME.textSecondary }}>Data</div>
                  <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>
                    {selectedPlanDetails.Data || "N/A"}
                  </div>
                </div>
                <div className="p-3 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                  <div className="text-xs mb-1" style={{ color: APP_THEME.textSecondary }}>Validity</div>
                  <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>
                    {selectedPlanDetails.Validity || "N/A"}
                  </div>
                </div>
              </div>

              {/* Talktime if available */}
              {selectedPlanDetails.Talktime && selectedPlanDetails.Talktime !== "N/A" && (
                <div className="mb-4 p-3 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                  <div className="text-xs mb-1" style={{ color: APP_THEME.textSecondary }}>Talktime</div>
                  <div className="text-sm font-semibold" style={{ color: APP_THEME.textPrimary }}>
                    {selectedPlanDetails.Talktime}
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="mb-4">
                <div className="text-sm font-semibold mb-2" style={{ color: APP_THEME.textPrimary }}>Description</div>
                <div className="text-xs leading-relaxed" style={{ color: APP_THEME.textSecondary }}>
                  {selectedPlanDetails.planDescription?.split('\n').map((line, idx) => (
                    <p key={idx} className="mb-1">{line}</p>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              {selectedPlanDetails.planBenefitItemList && selectedPlanDetails.planBenefitItemList.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold mb-2" style={{ color: APP_THEME.textPrimary }}>Benefits</div>
                  <div className="space-y-2">
                    {selectedPlanDetails.planBenefitItemList.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: APP_THEME.surfaceColor }}>
                        {item.logoUrl && (
                          <img src={item.logoUrl} alt="" className="w-5 h-5 object-contain" />
                        )}
                        <span className="text-xs" style={{ color: APP_THEME.textSecondary }}>{item.displayName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Select Button */}
            <div className="sticky bottom-0 p-4 border-t" style={{ background: APP_THEME.bgColor, borderColor: APP_THEME.borderColor }}>
              <button
                onClick={() => {
                  handleSelect(selectedPlanDetails);
                  setShowDetailsSheet(false);
                }}
                className="w-full py-3 rounded-xl text-base font-bold transition-all active:scale-98"
                style={{
                  background: APP_THEME.primaryColor,
                  color: '#fff',
                }}
              >
                Select Plan
              </button>
            </div>
          </div>
        </>
      )}

      {/* Operator Modal - Keeping for backward compatibility but disabled since we have inline edit */}
      {showOperatorModal && (
        <div
          className="fixed inset-0 bg-black/50 z-[1100] flex items-end justify-center animate-fadeIn"
          onClick={() => setShowOperatorModal(false)}
        >
          <div
            className="w-full max-w-[480px] rounded-t-2xl p-5 animate-slideUp"
            style={{ background: APP_THEME.bgColor }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: APP_THEME.borderColor }} />
            <h3 className="text-lg font-bold mb-4 text-center" style={{ color: APP_THEME.textPrimary }}>Select Operator</h3>
            <div className="flex flex-col gap-2">
              {operators.map((operatorItem) => (
                <div
                  key={operatorItem.name}
                  onClick={() => {
                    setSelectedOperator(operatorItem);
                    setShowOperatorModal(false);
                  }}
                  className="flex items-center gap-3 p-3 rounded-xl cursor-pointer active:scale-98 transition-transform"
                  style={{
                    background: selectedOperator.name === operatorItem.name ? `${operatorItem.color}10` : APP_THEME.surfaceColor,
                    border: `1px solid ${selectedOperator.name === operatorItem.name ? operatorItem.color : APP_THEME.borderColor}`,
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold"
                    style={{ background: `${operatorItem.color}15`, color: operatorItem.color }}
                  >
                    {operatorItem.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold" style={{ color: APP_THEME.textPrimary }}>{operatorItem.name}</div>
                    <div className="text-xs" style={{ color: APP_THEME.textSecondary }}>Click to select</div>
                  </div>
                  {selectedOperator.name === operatorItem.name && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={operatorItem.color} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowOperatorModal(false)}
              className="w-full mt-4 py-2.5 rounded-xl border text-sm font-semibold active:scale-98 transition-transform"
              style={{
                background: APP_THEME.surfaceColor,
                color: APP_THEME.textPrimary,
                borderColor: APP_THEME.borderColor,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <Toaster position="bottom-center" />
      <style>
        {`
          .active\\:scale-98:active { transform: scale(0.98); }
          .active\\:scale-95:active { transform: scale(0.95); }
          .active\\:scale-90:active { transform: scale(0.9); }
          .scrollbar-hide::-webkit-scrollbar { display: none; }
          .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .animate-fadeIn { animation: fadeIn 0.2s ease forwards; }
          .animate-slideUp { animation: slideUp 0.3s ease forwards; }
        `}
      </style>
    </div>
  );
}

export default MobileRechargePlan;